import React, { useState, useEffect } from 'react';
import {
  Button,
  ButtonType,
  ButtonSize,
  Icon,
  IconName,
  IconButton,
  IconButtonVariant,
  IconButtonSize,
  TagVariant,
  Tag,
} from '@hills-design-system/components';
import { AdoptionCounts } from '../../types/adoptionReports';
import { formatTimeframeToMonth, calculateTotalAdoptions } from '../../utils/timeframeHelpers';
import { PetFaceDogIcon, PetFaceCatIcon, CheckCircleIcon } from '../icons';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

interface AdoptionReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (adoptionCounts: AdoptionCounts) => void;
  timeframeStart: string;
  lastEdited: string;
  initialCounts?: AdoptionCounts;
  title?: string;
  isEditing?: boolean;
}

const AdoptionReportModal: React.FC<AdoptionReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  timeframeStart,
  lastEdited,
  initialCounts,
  title,
  isEditing,
}) => {
  const [counts, setCounts] = useState<AdoptionCounts>({
    adultSeniorDogs: 0,
    puppies: 0,
    adultSeniorCats: 0,
    kittens: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [emptyFields, setEmptyFields] = useState<Set<keyof AdoptionCounts>>(new Set());
  const { t } = useTranslation();

  // Initialize counts when modal opens or initial data changes
  useEffect(() => {
    if (isOpen) {
      setCounts(
        initialCounts ?? {
          adultSeniorDogs: 0,
          puppies: 0,
          adultSeniorCats: 0,
          kittens: 0,
        }
      );
      setHasChanges(false); // Reset change tracking when modal opens
      setEmptyFields(new Set());
    }
  }, [isOpen, initialCounts]);

  const updateCount = (field: keyof AdoptionCounts, delta: number) => {
    setCounts((prev) => {
      const updatedCounts = {
        ...prev,
        [field]: Math.max(0, prev[field] + delta),
      };
      setHasChanges(JSON.stringify(updatedCounts) !== JSON.stringify(initialCounts)); // Check for changes
      return updatedCounts;
    });
  };

  const handleDirectInput = (field: keyof AdoptionCounts, value: string) => {
    // Allow empty string for editing without forcing a 0 into the field (which
    // would otherwise get prepended to the next digit typed, e.g. "05")
    if (value === '') {
      setEmptyFields((prev) => new Set(prev).add(field));
      return;
    }

    setEmptyFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });

    const numValue = parseInt(value, 10);
    // Only update if it's a valid positive integer
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 9999) {
      setCounts((prev) => {
        const updatedCounts = {
          ...prev,
          [field]: numValue,
        };
        setHasChanges(JSON.stringify(updatedCounts) !== JSON.stringify(initialCounts)); // Check for changes
        return updatedCounts;
      });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(counts);
      setHasChanges(false);
      setIsSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting adoption report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsSubmitSuccess(false);
    if (hasChanges) {
      setShowDiscardConfirmation(true); // Show confirmation dialog if changes exist
    } else {
      onClose(); // Close modal directly if no changes
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirmation(false);
    setHasChanges(false);
    onClose();
  };

  const cancelDiscard = () => {
    setShowDiscardConfirmation(false);
  };

  const totalAdoptions = calculateTotalAdoptions(counts);
  const monthDisplay = formatTimeframeToMonth(timeframeStart);
  const lastEditedDisplay = lastEdited ? format(parseISO(lastEdited), 'MMM. d, yyyy') : '-';
  const canineTotal = counts.adultSeniorDogs + counts.puppies;
  const felineTotal = counts.adultSeniorCats + counts.kittens;

  if (!isOpen) return null;

  // Determine title and button label based on isEditing
  const modalTitle = title ?? (isEditing ? t(TranslationKeyEnum.EditMonthAdoptions, monthDisplay) : t(TranslationKeyEnum.SubmitMonthAdoptions, monthDisplay));
  const submitButtonLabel = isEditing
    ? t(TranslationKeyEnum.SubmitChanges)
    : t(TranslationKeyEnum.SubmitReport);

  const handleFocus = (field: keyof AdoptionCounts) => {
    if (counts[field] === 0) {
      setEmptyFields((prev) => new Set(prev).add(field));
    }
  };

  const handleBlur = (field: keyof AdoptionCounts) => {
    setEmptyFields((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
    if (emptyFields.has(field)) {
      setCounts((prev) => {
        const updatedCounts = {
          ...prev,
          [field]: 0,
        };
        setHasChanges(JSON.stringify(updatedCounts) !== JSON.stringify(initialCounts));
        return updatedCounts;
      });
    }
  };

  const getFieldValue = (field: keyof AdoptionCounts) => (emptyFields.has(field) ? '' : counts[field]);
  const formatTotal = (num: number) => {
    if (num >= 10000) {
      // Divide by 1000 and round to 1 decimal place if it's not a whole number
      const formatted = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
      return `${formatted}K`;
    }
    return num.toString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/50 border-0 p-0 cursor-default"
        onClick={closeModal}
        aria-label="Close Modal"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-800 shadow-400 w-[900px] max-w-[90vw] max-h-[90vh] overflow-hidden mx-200">
        {/* Header */}
        <div className="flex justify-between items-stretch gap-60 p-200 pb-0">
          {/* Left side - empty for layout */}
          <div className="flex-1" />

          {/* Close Button */}
          <div className="w-300 h-300">
            <IconButton
              icon={IconName.X}
              variant={IconButtonVariant.Secondary}
              size={IconButtonSize.Medium}
              onClick={closeModal}
              ariaLabel="Close modal"
              className="w-full h-full rounded-round shadow-100 bg-gray-50"
            />
          </div>
        </div>

        {/* Content */}
        {!isSubmitSuccess && !showDiscardConfirmation && (
          <div className="flex flex-col gap-60 px-200 pb-200 h-full overflow-auto max-h-[calc(90vh-100px)]">
            {/* Header Section */}
            <div className="flex flex-col gap-100">
              <div className="flex items-center gap-40">
                <p className="text-detail-90 font-bold text-black leading-detail">
                  {t(TranslationKeyEnum.AdoptionReportSingular)}
                </p>
              </div>
              <div className="flex justify-between items-end gap-75">
                <div className="flex flex-col gap-75">
                  <h2 className="text-heading-200 font-bold text-black leading-heading">
                    {modalTitle}
                  </h2>
                  <p className="font-normal text-base text-black-600">
                    {t(TranslationKeyEnum.AdoptionModalSubText)}
                  </p>
                </div>
                {/* Download Template Button - Hidden as per Figma */}
                <Button
                  label={t(TranslationKeyEnum.DownloadTemplate)}
                  type={ButtonType.Secondary}
                  size={ButtonSize.Small}
                  onClick={() => {}}
                  className="opacity-0"
                />
              </div>
            </div>

            {/* Inputs Section */}
            <div className="flex flex-col gap-50">
              {/* Accordion Card */}
              <div className="bg-gray-50 rounded-400 p-200 flex flex-col gap-200">
                {/* Accordion Header */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-50 h-150">
                    <h3 className="text-body-100 font-semibold text-black leading-action">
                      {t(TranslationKeyEnum.MonthTotalAdoptions, monthDisplay, totalAdoptions)}
                    </h3>
                  </div>
                  <div className="flex justify-end items-center gap-100 w-[341px]">
                    {/* Status area - hidden as per Figma */}
                    {isEditing && (
                      <div className="flex gap-4 items-center">
                        <p className="text-detail-90 font-medium text-gray-700 leading-detail">
                          {t(TranslationKeyEnum.LastEdited)} {lastEditedDisplay}
                        </p>
                        <Tag label={t(TranslationKeyEnum.Submitted)} variant={TagVariant.Green} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-0 border-t border-black/20" />

                {/* Numbers Section */}
                <div className="flex gap-200 w-full">
                  {/* Canine Section */}
                  <div className="flex flex-col justify-between gap-[27px] w-[348px]">
                    {/* Canine Total */}
                    <div className="flex justify-between items-start ">
                      <div className="flex items-center gap-100">
                        <PetFaceDogIcon
                          width={32}
                          height={32}
                          fill="currentColor"
                          className="text-black"
                        />
                        <div className="w-[150px]">
                          <h4 className="text-heading-200 font-bold text-black leading-heading">
                            {t(TranslationKeyEnum.Canine)}
                          </h4>
                        </div>
                      </div>
                      <div className="min-w-[175px] text-right">
                        <span
                          className={clsx(
                            'font-bold text-black leading-tight break-all ',
                            formatTotal(canineTotal).length <= 3
                              ? 'text-display-500'
                              : 'text-display-350'
                          )}
                        >
                          {formatTotal(canineTotal)}
                        </span>
                      </div>
                    </div>

                    {/* Canine Steppers */}
                    <div className="flex flex-col gap-100">
                      {/* Adult & Senior Dogs */}
                      <div className="flex justify-between items-center gap-[34px]">
                        <div className="w-[234px]">
                          <span className="text-body-100 font-semibold text-black leading-action">
                            {t(TranslationKeyEnum.AdultSeniorDogs)}
                          </span>
                        </div>
                        <div className="flex items-stretch w-[200px] h-[40px] border border-primary rounded-200 bg-white">
                          <button
                            onClick={() => updateCount('adultSeniorDogs', -1)}
                            disabled={counts.adultSeniorDogs === 0}
                            className="flex-1 flex items-center justify-center border-r border-blue-100 max-w-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon
                              icon={IconName.Minus}
                              size={18}
                              className={
                                counts.adultSeniorDogs === 0 ? 'text-black/20' : 'text-primary'
                              }
                            />
                          </button>
                          <div className="flex-1 flex items-center justify-center">
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              value={getFieldValue('adultSeniorDogs')}
                              onFocus={() => handleFocus('adultSeniorDogs')}
                              onBlur={() => handleBlur('adultSeniorDogs')}
                              onChange={(e) => handleDirectInput('adultSeniorDogs', e.target.value)}
                              className="text-body-100 font-bold text-primary leading-detail text-center w-full bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <button
                            onClick={() => updateCount('adultSeniorDogs', 1)}
                            className="flex-1 flex items-center justify-center border-l border-blue-100 max-w-[40px]"
                          >
                            <Icon icon={IconName.Plus} size={26} className="text-primary" />
                          </button>
                        </div>
                      </div>

                      {/* Puppies */}
                      <div className="flex justify-between items-center gap-[34px]">
                        <div className="w-[234px]">
                          <span className="text-body-100 font-semibold text-black leading-action">
                            {t(TranslationKeyEnum.PuppiesLessThan1Year)}
                          </span>
                        </div>
                        <div className="flex items-stretch w-[200px] h-[40px] border border-primary rounded-200 bg-white">
                          <button
                            onClick={() => updateCount('puppies', -1)}
                            disabled={counts.puppies === 0}
                            className="flex-1 flex items-center justify-center border-r border-blue-100 max-w-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon
                              icon={IconName.Minus}
                              size={18}
                              className={counts.puppies === 0 ? 'text-black/20' : 'text-primary'}
                            />
                          </button>
                          <div className="flex-1 flex items-center justify-center">
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              value={getFieldValue('puppies')}
                              onFocus={() => handleFocus('puppies')}
                              onBlur={() => handleBlur('puppies')}
                              onChange={(e) => handleDirectInput('puppies', e.target.value)}
                              className="text-body-100 font-bold text-primary leading-detail text-center w-full bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <button
                            onClick={() => updateCount('puppies', 1)}
                            className="flex-1 flex items-center justify-center border-l border-blue-100 max-w-[40px]"
                          >
                            <Icon icon={IconName.Plus} size={26} className="text-primary" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vertical Divider */}
                  <div className="w-0 h-[241px] border-l border-black/20" />

                  {/* Feline Section */}
                  <div className="flex flex-col justify-between gap-[27px] w-[348px]">
                    {/* Feline Total */}
                    <div className="flex justify-between items-start ">
                      <div className="flex items-center gap-100">
                        <PetFaceCatIcon
                          width={32}
                          height={32}
                          fill="currentColor"
                          className="text-black"
                        />
                        <div className="w-[150px]">
                          <h4 className="text-heading-200 font-bold text-black leading-heading">
                            {t(TranslationKeyEnum.Feline)}
                          </h4>
                        </div>
                      </div>
                      <div className="min-w-[175px] text-right">
                        <span
                          className={clsx(
                            'font-bold text-black leading-tight break-all ',
                            formatTotal(felineTotal).length <= 3
                              ? 'text-display-500'
                              : 'text-display-350'
                          )}
                        >
                          {formatTotal(felineTotal)}
                        </span>
                      </div>
                    </div>

                    {/* Feline Steppers */}
                    <div className="flex flex-col gap-100">
                      {/* Adult & Senior Cats */}
                      <div className="flex justify-between items-center gap-[34px]">
                        <div className="w-[234px]">
                          <span className="text-body-100 font-semibold text-black leading-action">
                            {t(TranslationKeyEnum.AdultSeniorCats)}
                          </span>
                        </div>
                        <div className="flex items-stretch w-[200px] h-[40px] border border-primary rounded-200 bg-white">
                          <button
                            onClick={() => updateCount('adultSeniorCats', -1)}
                            disabled={counts.adultSeniorCats === 0}
                            className="flex-1 flex items-center justify-center border-r border-blue-100 max-w-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon
                              icon={IconName.Minus}
                              size={18}
                              className={
                                counts.adultSeniorCats === 0 ? 'text-black/20' : 'text-primary'
                              }
                            />
                          </button>
                          <div className="flex-1 flex items-center justify-center">
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              value={getFieldValue('adultSeniorCats')}
                              onFocus={() => handleFocus('adultSeniorCats')}
                              onBlur={() => handleBlur('adultSeniorCats')}
                              onChange={(e) => handleDirectInput('adultSeniorCats', e.target.value)}
                              className="text-body-100 font-bold text-primary leading-detail text-center w-full bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <button
                            onClick={() => updateCount('adultSeniorCats', 1)}
                            className="flex-1 flex items-center justify-center border-l border-blue-100 max-w-[40px]"
                          >
                            <Icon icon={IconName.Plus} size={26} className="text-primary" />
                          </button>
                        </div>
                      </div>

                      {/* Kittens */}
                      <div className="flex justify-between items-center gap-[34px]">
                        <div className="w-[234px]">
                          <span className="text-body-100 font-semibold text-black leading-action">
                            {t(TranslationKeyEnum.KittensLessThan1Year)}
                          </span>
                        </div>
                        <div className="flex items-stretch w-[200px] h-[40px] border border-primary rounded-200 bg-white">
                          <button
                            onClick={() => updateCount('kittens', -1)}
                            disabled={counts.kittens === 0}
                            className="flex-1 flex items-center justify-center border-r border-blue-100 max-w-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Icon
                              icon={IconName.Minus}
                              size={18}
                              className={counts.kittens === 0 ? 'text-black/20' : 'text-primary'}
                            />
                          </button>
                          <div className="flex-1 flex items-center justify-center">
                            <input
                              type="number"
                              min="0"
                              max="9999"
                              value={getFieldValue('kittens')}
                              onFocus={() => handleFocus('kittens')}
                              onBlur={() => handleBlur('kittens')}
                              onChange={(e) => handleDirectInput('kittens', e.target.value)}
                              className="text-body-100 font-bold text-primary leading-detail text-center w-full bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                          <button
                            onClick={() => updateCount('kittens', 1)}
                            className="flex-1 flex items-center justify-center border-l border-blue-100 max-w-[40px]"
                          >
                            <Icon icon={IconName.Plus} size={26} className="text-primary" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button Section */}
                <div className="flex flex-col items-center gap-100">
                  {/* Hidden Divider */}
                  <div className="w-full h-0 border-t border-black opacity-0" />

                  {/* Submit Button */}
                  <Button
                    label={isSubmitting ? t(TranslationKeyEnum.Submitting) : submitButtonLabel}
                    type={ButtonType.Primary}
                    size={ButtonSize.Small}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    icon={isSubmitting ? IconName.Refresh : ''}
                    className="rounded-200 h-250"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {isSubmitSuccess && !showDiscardConfirmation && (
          <div className="flex flex-col items-center justify-center w-full pb-14">
            <div className="flex flex-col items-center gap-6 w-full">
              <CheckCircleIcon className="text-primary" width={56} height={56} />
              <div className="flex flex-col items-center gap-4">
                <h2 className="font-montserrat font-semibold text-2xl leading-[1.125em] tracking-[-1.5%] text-primary text-center whitespace-pre-line">
                  {t(TranslationKeyEnum.AdoptionReportSuccessHeader, monthDisplay)}
                </h2>
              </div>
            </div>
            <div className="mt-8">
              <Button
                label={t(TranslationKeyEnum.BackToReports)}
                type={ButtonType.Primary}
                size={ButtonSize.Small}
                onClick={closeModal}
              />
            </div>
          </div>
        )}

        {/* Discard Confirmation Dialog */}
        {showDiscardConfirmation && (
          <div className="flex flex-col gap-60 px-200 pb-500 items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-3 w-[400px]">
              <h3 className="text-heading-150 font-bold text-black mb-0">
                {t(TranslationKeyEnum.DiscardReport)}
              </h3>
              <p className="font-normal text-center text-gray-500 mb-100">
                {t(TranslationKeyEnum.DiscardConfirmationMessage)}
              </p>
              <div className="flex justify-end gap-100">
                <Button
                  label={t(TranslationKeyEnum.GoBack)}
                  type={ButtonType.Secondary}
                  size={ButtonSize.Small}
                  onClick={cancelDiscard}
                />
                <Button
                  label={t(TranslationKeyEnum.YesDiscard)}
                  type={ButtonType.Primary}
                  size={ButtonSize.Small}
                  onClick={confirmDiscard}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdoptionReportModal;
